/**
 * useContractValidation Hook
 * Provides step-by-step validation for the contract wizard
 */

import { useCallback } from 'react';
import { useContractFormContext } from '../components/contracts/wizard/ContractFormContext';
import { contractValidation } from '../utils/validation';

/**
 * Step validation configurations
 */
const STEP_VALIDATIONS = {
  // Step 0: Contract Details
  0: {
    // maturity_date is auto-derived from deposit + term, but the derivation can fail to run
    // (e.g. term entered before a deposit date, or an edited Draft where it was never stored).
    // The API hard-requires it (POST /contracts) and the SP enforces maturity > deposit, so
    // gate on it here too — otherwise Step 0 passes and the save 400s late.
    requiredFields: ['product_id', 'capital_amount', 'investment_currency_id', 'investment_term_value', 'deposit_date', 'maturity_date'],
    conditionalRequired: {
      investor_id: (values) => !values.is_multi_investor,
      rolled_from_contract_id: (values) => parseFloat(values.rolled_funds_amount) > 0 && !values.is_multi_investor,
    },
    fieldLabels: {
      product_id: 'Product type',
      capital_amount: 'Capital amount',
      investment_currency_id: 'Currency',
      investment_term_value: 'Investment term',
      deposit_date: 'Deposit date',
      maturity_date: 'Maturity date',
      investor_id: 'Investor',
      rolled_from_contract_id: 'Prior contract',
    },
  },
  // Step 1: Interest & Payments
  1: {
    // interest_rate handled by an explicit > 0 guard in case 1 below (the
    // generic empty-check treats 0 / '0.0000' as a valid value).
    requiredFields: ['rate_type', 'calculation_profile_id', 'day_count_basis', 'interest_paid_frequency_id'],
    conditionalRequired: {
      benchmark_rate_id: (values) => values.rate_type === 'Variable' || values.rate_type === 'Floating',
      repricing_frequency_id: (values) => !!values.eligible_for_repricing,
      // Renewal freq/date are enforced by the SP when eligible_for_renewal is on; mirror the
      // repricing pair here so the central validator gates them (not just inline RHF rules).
      renewal_frequency_id: (values) => !!values.eligible_for_renewal,
      first_renewal_date: (values) => !!values.eligible_for_renewal,
    },
    fieldLabels: {
      rate_type: 'Rate type',
      calculation_profile_id: 'Calculation profile',
      day_count_basis: 'Day count basis',
      interest_paid_frequency_id: 'Interest payment frequency',
      benchmark_rate_id: 'Benchmark rate',
      repricing_frequency_id: 'Re-pricing frequency',
      renewal_frequency_id: 'Renewal frequency',
      first_renewal_date: 'First renewal date',
    },
  },
  // Step 2: Adhoc Events — entirely optional; populated rows must be complete
  2: {
    requiredFields: [],
    conditionalRequired: {},
    fieldLabels: {},
  },
  // Step 3: Fees & Security — toggle-gated optional sections
  3: {
    requiredFields: [],
    conditionalRequired: {
      broker_id: (values) => values.is_brokered,
      broker_fees: (values) => values.is_brokered,
      // T112: stamp duty is standalone — required whenever its toggle is on,
      // regardless of whether the contract is brokered.
      stamp_duty_fees: (values) => !!values.stamp_duty,
      collateral_provider_id: (values) => values.has_collateral,
      collateral_type_id: (values) => values.has_collateral,
      collateral_amount: (values) => values.has_collateral,
    },
    fieldLabels: {
      broker_id: 'Broker',
      broker_fees: 'Broker fees',
      stamp_duty_fees: 'Stamp duty fees',
      collateral_provider_id: 'Collateral provider',
      collateral_type_id: 'Collateral type',
      collateral_amount: 'Collateral amount',
    },
  },
  // Step 4: Review & Schedule
  4: {
    requiredFields: [],
    conditionalRequired: {},
    fieldLabels: {},
  },
};

/**
 * Format field name for display
 */
const formatFieldName = (fieldName) => {
  return fieldName
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export function useContractValidation() {
  const {
    contractInvestors,
    adhocInterestPayments,
    adhocCapitalEvents,
    hasSchedule,
    isEdit,
    refData,
    programRequirement,
    rolledFundsError,
    requireLikelihoodToRoll,
    setValidationErrors,
    setValidationWarnings,
  } = useContractFormContext();

  /**
   * Validate a specific step
   */
  const validateStep = useCallback((step, formValues) => {
    const errors = [];
    const warnings = [];
    const validation = STEP_VALIDATIONS[step];

    if (!validation) {
      return { valid: true, errors: [], warnings: [] };
    }

    // Check required fields
    validation.requiredFields.forEach((field) => {
      const value = formValues[field];
      if (value === undefined || value === null || value === '') {
        const label = validation.fieldLabels[field] || formatFieldName(field);
        errors.push(`${label} is required`);
      }
    });

    // Check conditional required fields
    if (validation.conditionalRequired) {
      Object.entries(validation.conditionalRequired).forEach(([field, condition]) => {
        if (condition(formValues)) {
          const value = formValues[field];
          if (value === undefined || value === null || value === '') {
            const label = validation.fieldLabels[field] || formatFieldName(field);
            errors.push(`${label} is required`);
          }
        }
      });
    }

    // Step-specific validations
    switch (step) {
      case 0: // Contract Details
        // Multi-investor validation
        if (formValues.is_multi_investor && contractInvestors.length === 0) {
          errors.push('At least one investor is required for multi-investor contracts');
        }

        // Likelihood to Roll requirement (tenant setting). When enabled, the
        // single-investor field must be set, and every investor on a
        // multi-investor contract must have an LTR value.
        if (requireLikelihoodToRoll) {
          if (formValues.is_multi_investor) {
            if (contractInvestors.some(inv => !inv.likelihood_to_roll)) {
              errors.push('Likelihood to Roll is required for every investor');
            }
          } else if (!formValues.likelihood_to_roll) {
            errors.push('Likelihood to Roll is required');
          }
        }

        // Program is mandatory once the chosen product is known to have linked
        // programs. Block while the list is still resolving so a fast Next
        // can't slip past before the requirement is known.
        if (programRequirement?.loading) {
          errors.push('Checking programs for this product…');
        } else if (programRequirement?.required && !formValues.program_id) {
          errors.push('Program is required for this product');
        }

        // Rolled-funds cap (single-investor): block Next when the captured rolled
        // amount exceeds the selected prior contract's remaining balance (or a
        // cross-currency source has no FX rate). Published by ContractDetailsStep
        // from the same computeRolledExceed check that drives the inline field error.
        if (rolledFundsError) {
          errors.push(rolledFundsError);
        }

        // Maturity date validation
        if (formValues.deposit_date && formValues.maturity_date) {
          const maturityResult = contractValidation.validateMaturityDate(
            formValues.deposit_date,
            formValues.maturity_date
          );
          if (!maturityResult.valid) {
            errors.push(maturityResult.message);
          }
        }

        // Capital amount validation
        if (formValues.capital_amount && formValues.investment_currency_id) {
          const currency = refData.currencies?.find(c => c.id === formValues.investment_currency_id);
          if (currency) {
            const amountResult = contractValidation.validateCapitalAmount(
              formValues.capital_amount,
              currency.currency_code
            );
            if (!amountResult.valid) {
              errors.push(amountResult.message);
            }
          }
        }

        // First payment date must fall within [deposit, maturity]
        if (formValues.first_payment_date) {
          const fpd = new Date(formValues.first_payment_date);
          if (formValues.deposit_date && fpd < new Date(formValues.deposit_date)) {
            errors.push('First payment date must be on or after start date');
          }
          if (formValues.maturity_date && fpd > new Date(formValues.maturity_date)) {
            errors.push('First payment date must be on or before maturity date');
          }
        }

        // Funds received date sanity checks. The engine refuses to generate
        // a schedule when funds_received_date lands AFTER the first capital
        // payment date (would produce capital payments before any interest
        // has accrued). The capital frequency lives on Step 1, so we can't
        // do the full check here — but we catch the common year-typo case
        // (funds-received more than 31 days after deposit) and the must-be-
        // within-term range so the user gets feedback at the earliest point.
        if (formValues.funds_received_date && formValues.deposit_date) {
          const frd = new Date(formValues.funds_received_date);
          const dep = new Date(formValues.deposit_date);
          if (frd < dep) {
            errors.push('Funds received date must be on or after start date');
          } else if (formValues.maturity_date && frd > new Date(formValues.maturity_date)) {
            errors.push('Funds received date must be on or before maturity date');
          } else {
            const daysAfterDeposit = Math.round((frd - dep) / (1000 * 60 * 60 * 24));
            if (daysAfterDeposit > 31) {
              errors.push(
                `Funds received date is ${daysAfterDeposit} days after the start date — ` +
                `this is unusually long and is often a year-typo. Funds are typically ` +
                `received within a month of contract start. Check the year.`
              );
            }
          }
        }
        break;

      case 1: // Interest & Payments
        // Day count basis is auto-filled from the chosen calculation profile (it has no field
        // of its own). The contract-create SP only accepts '360' or '365'; if a profile carries
        // any other value (e.g. 'Actual/365'), the save 400s late. Surface it HERE — at the step
        // that owns the profile — with an actionable message rather than silently rewriting a
        // financial parameter or deferring to a server error.
        if (formValues.day_count_basis
            && !['360', '365'].includes(String(formValues.day_count_basis))) {
          errors.push(
            `The selected calculation profile uses a day-count basis of "${formValues.day_count_basis}", `
            + `which this contract type can't use (only 360 or 365 are supported). `
            + `Pick a profile with a 360 or 365 basis.`
          );
        }

        // Interest rate must be > 0 (the generic required-check treats 0 / '0.0000' as valid)
        {
          const rateNum = parseFloat(formValues.interest_rate);
          if (formValues.interest_rate === '' || formValues.interest_rate == null || isNaN(rateNum) || rateNum <= 0) {
            errors.push('Interest rate must be greater than 0');
          } else if (formValues.rate_type) {
            const rateResult = contractValidation.validateInterestRate(
              formValues.interest_rate,
              formValues.rate_type
            );
            if (!rateResult.valid) {
              errors.push(rateResult.message);
            } else if (rateResult.warning) {
              warnings.push(rateResult.warning);
            }
          }
        }

        // Re-pricing frequency richer rules (mirrors Controller validate at
        // InterestPaymentsStep.js:627-646). Adhoc not allowed; cannot exceed term.
        if (formValues.eligible_for_repricing && formValues.repricing_frequency_id) {
          const selected = (refData.frequencies || []).find(
            f => f.id === parseInt(formValues.repricing_frequency_id, 10)
          );
          if (selected) {
            if (selected.frequency && selected.frequency.toLowerCase() === 'adhoc') {
              errors.push('Adhoc cannot be used for re-pricing');
            } else if (formValues.deposit_date && formValues.maturity_date && selected.months_value) {
              const termMonths = Math.round(
                (new Date(formValues.maturity_date) - new Date(formValues.deposit_date))
                / (1000 * 60 * 60 * 24 * 30.44)
              );
              if (selected.months_value > termMonths) {
                errors.push('Re-pricing frequency cannot exceed the contract term');
              }
            }
          }
        }

        // T3-068: specified_date rate-change basis (Variable only) requires a fixed-cadence
        // frequency + a first date strictly inside the term. Mirrors the SP guard + the Controller
        // rules on InterestPaymentsStep so the Next button gates on it too.
        if (formValues.rate_type === 'Variable' && formValues.rate_change_effective_basis === 'specified_date') {
          // T3-098: a CUSTOM interval fully replaces the frequency — its resolved month count is
          // what the engine uses (fn_contract_rate_change_months), so demanding a frequency the
          // system will then ignore is a dead field. Either source satisfies the cadence.
          const customOn = (formValues.rate_change_custom_unit === 'months'
              || formValues.rate_change_custom_unit === 'years')
            && parseInt(formValues.rate_change_custom_value, 10) > 0;
          if (!formValues.rate_change_frequency_id && !customOn) {
            errors.push('Rate change frequency is required for the "On Specified Date" basis (or enable a custom interval)');
          } else if (customOn) {
            // Validate the CUSTOM interval instead; the frequency is not consulted.
            const n = parseInt(formValues.rate_change_custom_value, 10);
            const months = n * (formValues.rate_change_custom_unit === 'years' ? 12 : 1);
            if (formValues.deposit_date && formValues.maturity_date) {
              const termMonths = Math.round(
                (new Date(formValues.maturity_date) - new Date(formValues.deposit_date))
                / (1000 * 60 * 60 * 24 * 30.44)
              );
              if (months > termMonths) {
                errors.push(`Custom rate change interval (${months} months) cannot exceed the contract term`);
              }
            }
          } else {
            const selected = (refData.frequencies || []).find(
              f => f.id === parseInt(formValues.rate_change_frequency_id, 10)
            );
            if (selected) {
              if (selected.frequency && selected.frequency.toLowerCase() === 'adhoc') {
                errors.push('Adhoc cannot be used for a specified rate-change date');
              } else if (selected.months_value >= 998) {
                errors.push('At Maturity cannot be used for a specified rate-change date');
              } else if (formValues.deposit_date && formValues.maturity_date && selected.months_value) {
                const termMonths = Math.round(
                  (new Date(formValues.maturity_date) - new Date(formValues.deposit_date))
                  / (1000 * 60 * 60 * 24 * 30.44)
                );
                if (selected.months_value > termMonths) {
                  errors.push('Rate change frequency cannot exceed the contract term');
                }
              }
            }
          }
          if (!formValues.first_rate_change_date) {
            errors.push('First rate change date is required for the "On Specified Date" basis');
          } else {
            if (formValues.deposit_date && formValues.first_rate_change_date <= formValues.deposit_date) {
              errors.push('First rate change date must be after the deposit date');
            }
            if (formValues.maturity_date && formValues.first_rate_change_date >= formValues.maturity_date) {
              errors.push('First rate change date must be before the maturity date');
            }
          }
        }

        // Payment frequency validation
        if (formValues.interest_paid_frequency_id && formValues.capital_paid_frequency_id) {
          const interestFreq = refData.frequencies?.find(f => f.id === formValues.interest_paid_frequency_id);
          const capitalFreq = refData.frequencies?.find(f => f.id === formValues.capital_paid_frequency_id);
          if (interestFreq && capitalFreq) {
            const freqResult = contractValidation.validatePaymentFrequency(
              interestFreq.frequency,
              capitalFreq.frequency,
              interestFreq.months_value,
              capitalFreq.months_value
            );
            if (!freqResult.valid) {
              errors.push(freqResult.message);
            }
          }
        }

        // Mirror the engine's funds-received guard at Step 1, now that we
        // know the capital payment frequency. First capital payment date =
        // first_payment_date when set, else deposit_date + capital_frequency_months.
        // If funds-received lands after that, the engine refuses to generate.
        // Catching it here means the error surfaces on Next instead of
        // several steps later at schedule generation.
        if (formValues.funds_received_date && formValues.deposit_date && formValues.capital_paid_frequency_id) {
          const capFreq = refData.frequencies?.find(f => f.id === formValues.capital_paid_frequency_id);
          if (capFreq && capFreq.months_value && capFreq.months_value > 0 && capFreq.months_value < 998) {
            const dep = new Date(formValues.deposit_date);
            const frd = new Date(formValues.funds_received_date);
            let firstCapPaymentDate;
            if (formValues.first_payment_date) {
              firstCapPaymentDate = new Date(formValues.first_payment_date);
            } else {
              firstCapPaymentDate = new Date(dep);
              firstCapPaymentDate.setMonth(firstCapPaymentDate.getMonth() + capFreq.months_value);
            }
            if (frd > firstCapPaymentDate) {
              const fmt = (d) => d.toISOString().slice(0, 10);
              errors.push(
                `Funds received date (${fmt(frd)}) is after the first capital ` +
                `payment date (${fmt(firstCapPaymentDate)}). Interest has not ` +
                `accrued yet on the first capital payments. Check the funds ` +
                `received date, start date, or capital payment frequency.`
              );
            }
          }
        }
        break;

      case 2: // Adhoc Events
        // When Adhoc frequency is selected, warn if no adhoc interest payment dates added
        if (formValues.interest_paid_frequency_id) {
          const interestFreq = refData.frequencies?.find(f => f.id === parseInt(formValues.interest_paid_frequency_id));
          if (interestFreq && interestFreq.frequency === 'Adhoc' && (!adhocInterestPayments || adhocInterestPayments.filter(e => e.event_date).length === 0)) {
            warnings.push('Adhoc interest frequency selected but no payment dates added. Interest will be paid at maturity.');
          }
        }

        // Adhoc interest payment date validation
        if (adhocInterestPayments && adhocInterestPayments.length > 0) {
          const depositDate = formValues.deposit_date ? new Date(formValues.deposit_date) : null;
          const maturityDate = formValues.maturity_date ? new Date(formValues.maturity_date) : null;
          const seenDates = new Set();

          adhocInterestPayments.forEach((event, index) => {
            if (!event.event_date) return;
            const eventDate = new Date(event.event_date);

            // Check within contract term
            if (depositDate && eventDate <= depositDate) {
              errors.push(`Adhoc interest payment #${index + 1}: date must be after deposit date`);
            }
            if (maturityDate && eventDate > maturityDate) {
              errors.push(`Adhoc interest payment #${index + 1}: date must be on or before maturity date`);
            }

            // Check for duplicate dates
            const dateKey = event.event_date;
            if (seenDates.has(dateKey)) {
              errors.push(`Adhoc interest payment #${index + 1}: duplicate date ${dateKey}`);
            }
            seenDates.add(dateKey);
          });
        }

        // Adhoc capital event row validation. The section is optional —
        // zero rows is fine — but any row the user has added must be complete.
        if (adhocCapitalEvents && adhocCapitalEvents.length > 0) {
          const capitalRows = adhocCapitalEvents
            .map((event, idx) => ({ event, idx }))
            .filter(({ event }) => event.event_type !== 'interest_accrual');
          const depositDate = formValues.deposit_date ? new Date(formValues.deposit_date) : null;
          const maturityDate = formValues.maturity_date ? new Date(formValues.maturity_date) : null;
          const seenCapitalDates = new Set();

          capitalRows.forEach(({ event, idx }, position) => {
            const amountNum = parseFloat(event.amount);
            const hasAmount = event.amount !== '' && event.amount != null && !isNaN(amountNum);
            const rowTouched = event.event_date || hasAmount;
            if (!rowTouched) return;

            const label = `Capital event #${position + 1}`;
            if (!event.event_date) {
              errors.push(`${label}: date is required`);
            }
            if (!hasAmount) {
              errors.push(`${label}: amount is required`);
            } else if (amountNum === 0) {
              errors.push(`${label}: amount cannot be zero`);
            }

            if (event.event_date) {
              const eventDate = new Date(event.event_date);
              if (depositDate && eventDate < depositDate) {
                errors.push(`${label}: date must be on or after start date`);
              }
              if (maturityDate && eventDate > maturityDate) {
                errors.push(`${label}: date must be on or before maturity date`);
              }
              if (seenCapitalDates.has(event.event_date)) {
                errors.push(`${label}: duplicate date ${event.event_date}`);
              }
              seenCapitalDates.add(event.event_date);
            }
          });
        }
        break;

      case 3: // Fees & Security
        // Broker fees validation
        if (formValues.is_brokered && formValues.broker_fees) {
          const feesResult = contractValidation.validateBrokerFees(formValues.broker_fees);
          if (!feesResult.valid) {
            errors.push(feesResult.message);
          }
        }

        // Stamp duty validation
        if (formValues.stamp_duty && formValues.stamp_duty_fees) {
          const stampResult = contractValidation.validateStampDutyFees(formValues.stamp_duty_fees);
          if (!stampResult.valid) {
            errors.push(stampResult.message);
          }
        }

        // Collateral validation — when toggle is on, amount must be > 0. The
        // conditional-required check above catches empty values; this catches 0.
        if (
          formValues.has_collateral &&
          formValues.collateral_amount !== '' &&
          formValues.collateral_amount != null &&
          formValues.capital_amount
        ) {
          const collateralResult = contractValidation.validateCollateralAmount(
            formValues.collateral_amount,
            formValues.capital_amount
          );
          if (!collateralResult.valid) {
            errors.push(collateralResult.message);
          } else if (collateralResult.warning) {
            warnings.push(collateralResult.warning);
          }
        }
        break;

      case 4: // Review & Schedule
        // For submission, schedule must exist in edit mode
        // This is validated separately in the submit handler
        break;

      default:
        break;
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }, [contractInvestors, adhocInterestPayments, adhocCapitalEvents, refData, programRequirement, rolledFundsError, requireLikelihoodToRoll]);

  /**
   * Validate full contract before submission
   */
  const validateForSubmission = useCallback((formValues) => {
    // Build contract object for full validation
    const currency = refData.currencies?.find(c => c.id === formValues.investment_currency_id);
    const interestFreq = refData.frequencies?.find(f => f.id === formValues.interest_paid_frequency_id);
    const capitalFreq = refData.frequencies?.find(f => f.id === formValues.capital_paid_frequency_id);
    const product = refData.products?.find(p => p.id === formValues.product_id);

    const contractToValidate = {
      investor_id: formValues.is_multi_investor ? 'multi' : formValues.investor_id,
      product_type: product?.type,
      currency_code: currency?.currency_code,
      deposit_date: formValues.deposit_date,
      maturity_date: formValues.maturity_date,
      investment_term: formValues.investment_term,
      investment_term_value: formValues.investment_term_value,
      capital_amount: formValues.capital_amount,
      interest_rate: formValues.interest_rate,
      rate_type: formValues.rate_type,
      is_brokered: formValues.is_brokered,
      broker_fees: formValues.broker_fees,
      stamp_duty: formValues.stamp_duty,
      stamp_duty_fees: formValues.stamp_duty_fees,
      has_collateral: formValues.has_collateral,
      collateral_amount: formValues.collateral_amount,
      interest_paid_frequency: interestFreq?.frequency,
      capital_paid_frequency: capitalFreq?.frequency,
      interest_paid_frequency_months: interestFreq?.months_value,
      capital_paid_frequency_months: capitalFreq?.months_value,
    };

    const result = contractValidation.validateContract(contractToValidate);

    // Additional check for schedule in edit mode
    if (isEdit && !hasSchedule) {
      result.errors.push('Amortization schedule must be generated before submitting for approval');
      result.valid = false;
    }

    return result;
  }, [refData, isEdit, hasSchedule]);

  /**
   * Run validation and update context state
   */
  const runStepValidation = useCallback((step, formValues) => {
    const result = validateStep(step, formValues);
    setValidationErrors(result.errors);
    setValidationWarnings(result.warnings);
    return result;
  }, [validateStep, setValidationErrors, setValidationWarnings]);

  /**
   * Run full validation and update context state
   */
  const runFullValidation = useCallback((formValues) => {
    const result = validateForSubmission(formValues);
    setValidationErrors(result.errors);
    setValidationWarnings(result.warnings);
    return result;
  }, [validateForSubmission, setValidationErrors, setValidationWarnings]);

  return {
    validateStep,
    validateForSubmission,
    runStepValidation,
    runFullValidation,
    STEP_VALIDATIONS,
  };
}

export default useContractValidation;
